import { useCallback, useMemo } from 'react';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import {
  ExpressionPostfixOnColumnName,
  ExpressionSettingsLineType,
  IParsedExpressionColumn,
} from '../interfaces';
import {
  MAX_METRIC_CARD_COLUMN,
  MAX_METRIC_CARD_ROW,
  MIN_METRIC_CARD_WIDTH,
} from './utils';
import { mapComparisonKey } from '../../../utils/redux/mergeExpressionComparisonData';
import { UnreachableCaseError } from 'ts-essentials';
import { useChartContext } from '../ChartContext';

export type IMetricData = IParsedExpressionColumn & {
  displayValue: number;
  percentageChange?: number;
  compareValue?: number;
};

interface IReturn {
  data: IMetricData[][];
  cardWidthByRow: number[];
  hasHiddenMetrics: boolean;
}

const getPostfixKeyOnLineType = (lineType: ExpressionSettingsLineType) => {
  switch (lineType) {
    case ExpressionSettingsLineType.Ma:
      return ExpressionPostfixOnColumnName.maKey;
    case ExpressionSettingsLineType.Ma30:
      return ExpressionPostfixOnColumnName.ma30Key;
    case ExpressionSettingsLineType.Ms7:
      return ExpressionPostfixOnColumnName.ms7Key;
    case ExpressionSettingsLineType.Ms30:
      return ExpressionPostfixOnColumnName.ms30Key;
    case ExpressionSettingsLineType.CumulativeSum:
      return ExpressionPostfixOnColumnName.cumulativeKey;
    default:
      throw new UnreachableCaseError(lineType);
  }
};

export const useMetricData = (containerWidth: number | undefined): IReturn => {
  // use displayedSlices to find out comparison slices
  const {
    settings: { chartSettings },
    chartData,
    displayedSlices,
    metadata: { comparisonInfo },
  } = useChartContext();
  const { expressionSettings, singleDayLabel, displayedMeasureSettings } =
    chartSettings;
  assertCondition(chartData.queryType === QueryType.Expression);

  const calculatePercentageChange = useCallback(
    (
      originalColumn: IParsedExpressionColumn,
      comparisonColumn: IParsedExpressionColumn | undefined,
      dayIndex: number,
      lineType?: ExpressionSettingsLineType | '',
    ) => {
      let percentageChange: number | undefined;
      let compareValue: number | undefined;
      let postfixKey: string = '';

      if (lineType) {
        postfixKey = getPostfixKeyOnLineType(lineType);
      }

      const displayValue =
        Number(chartData.data[dayIndex][originalColumn.key + postfixKey]) ?? 0;
      if (comparisonColumn) {
        compareValue =
          Number(chartData.data[dayIndex][comparisonColumn.key + postfixKey]) ??
          0;
        percentageChange = (displayValue - compareValue) / displayValue;
      }

      return {
        percentageChange,
        displayValue,
        compareValue,
      };
    },
    [chartData],
  );

  const data = useMemo(() => {
    if (!containerWidth) {
      return { data: [], cardWidthByRow: [], hasHiddenMetrics: false };
    }

    // use visible measures and slices to collect 12 columns of data for displaying in the metric chart
    const visibleMeasureKeys = displayedMeasureSettings
      .filter((measure) => measure.isVisible)
      .map((measure) => measure.key);
    const visibleSliceKeys = displayedSlices
      .filter((slice) => slice.isVisible)
      .map((slice) => slice.key);

    // possible card count for current combination of measures and slices
    const maxPossibleCard = visibleMeasureKeys.length * visibleSliceKeys.length;
    // calculate card width and max visible card numbers by container width for the dynamic display
    const WIDTH_OFFSET = 10;
    // max card numbers can be displayed in a row
    const maxCardPerRow = Math.min(
      MAX_METRIC_CARD_COLUMN,
      Math.floor((containerWidth - WIDTH_OFFSET) / MIN_METRIC_CARD_WIDTH),
    );

    // find out how many cards can be displayed in the current container
    const maxCardNumbers = Math.min(
      maxPossibleCard,
      maxCardPerRow * MAX_METRIC_CARD_ROW,
    );

    // not efficient, but simple to keep the order by [displayedMeasure, displayedSlice]
    const visibleColumns: IParsedExpressionColumn[] = [];
    for (const visibleMeasure of visibleMeasureKeys) {
      for (const visibleSlice of visibleSliceKeys) {
        if (visibleColumns.length >= maxCardNumbers) {
          break;
        }
        const column = chartData.columns.find(
          (column) =>
            column.measureKey === visibleMeasure &&
            column.sliceKey === visibleSlice,
        );
        assertCondition(column);
        visibleColumns.push(column);
      }
    }

    // find out displayed columns and their display values
    const result: IReturn['data'] = [];
    const cardWidths: number[] = [];
    let colIndex = 0;

    while (colIndex < visibleColumns.length) {
      if (result.length >= MAX_METRIC_CARD_ROW) {
        break;
      }
      const row = [];

      for (
        let i = 0;
        i < maxCardPerRow && colIndex < visibleColumns.length;
        i++
      ) {
        const visibleColumn = visibleColumns[colIndex++];
        const valueIndex = chartData.dates.indexOf(singleDayLabel);
        assertCondition(valueIndex !== -1);

        const comparisonColumn: IParsedExpressionColumn | undefined =
          chartData.columns.find(
            (col) =>
              col.sliceKey === mapComparisonKey(visibleColumn.sliceKey) &&
              col.isComparison,
          );
        assertCondition(
          !comparisonInfo?.comparisonConfig || comparisonColumn,
          'comparisonColumn should be found when comparisonConfig is not null',
        );

        const { displayValue, compareValue, percentageChange } =
          calculatePercentageChange(
            visibleColumn,
            comparisonColumn,
            valueIndex,
            expressionSettings?.lineType,
          );

        // KBT-840: Filter out comparison groups
        if (!visibleColumn.isComparison) {
          row.push({
            ...visibleColumn,
            displayValue,
            percentageChange,
            compareValue,
          });
        }
      }

      // calculate card width for current row because the last row may have less cards than maxCardPerRow
      const cardsLeft = Math.max(
        maxCardNumbers - result.length * maxCardPerRow,
        0,
      );
      const cardWidth = Math.max(
        MIN_METRIC_CARD_WIDTH,
        (containerWidth - WIDTH_OFFSET) / Math.min(cardsLeft, maxCardPerRow),
      );
      cardWidths.push(cardWidth);

      result.push(row);
    }
    return {
      data: result,
      cardWidthByRow: cardWidths,
      hasHiddenMetrics: maxCardPerRow * MAX_METRIC_CARD_ROW < maxPossibleCard,
    };
  }, [
    comparisonInfo?.comparisonConfig,
    calculatePercentageChange,
    containerWidth,
    chartData,
    singleDayLabel,
    expressionSettings?.lineType,
    displayedMeasureSettings,
    displayedSlices,
  ]);

  return data;
};
